import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { parseManifest, type SyncStatus, type ManifestEntry } from "../lib/parse-manifest";
import { resolveRoute } from "../lib/resolve-route";
import { useWorkshopSearch } from "../workshop-search-context";
import syncManifestRaw from "../sync-manifest.yaml?raw";

// ─── Status config ────────────────────────────────────────

const STATUS_CONFIG: Record<
  SyncStatus,
  { label: string; color: string; bg: string; dot: string }
> = {
  synced: {
    label: "Synced",
    color: "text-emerald-400",
    bg: "bg-emerald-400/10 border-emerald-400/20",
    dot: "bg-emerald-400",
  },
  modified: {
    label: "Modified",
    color: "text-amber-400",
    bg: "bg-amber-400/10 border-amber-400/20",
    dot: "bg-amber-400",
  },
  new: {
    label: "New",
    color: "text-sky-400",
    bg: "bg-sky-400/10 border-sky-400/20",
    dot: "bg-sky-400",
  },
  "workshop-only": {
    label: "Workshop Only",
    color: "text-white/40",
    bg: "bg-white/5 border-white/10",
    dot: "bg-white/30",
  },
};

type FilterKey = "all" | SyncStatus;

// ─── Terminal command block ────────────────────────────────

function TerminalCommand({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(command).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div
      className={cn(
        "mt-2 flex items-center justify-between gap-3 rounded-lg border border-white/[0.06]",
        "bg-[#0d0d0f] px-4 py-2.5 font-mono text-xs",
      )}
      data-testid="terminal-command"
    >
      <div className="flex items-center gap-2 text-white/50">
        <span className="select-none text-accent/50">$</span>
        <span className="select-all">{command}</span>
      </div>
      <button
        type="button"
        onClick={handleCopy}
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded",
          "text-white/25 transition-colors hover:bg-white/5 hover:text-white/50",
          copied && "text-emerald-400",
        )}
        aria-label="Copy command"
        data-testid="terminal-command-copy"
      >
        {copied ? (
          <Icons.Check className="h-3.5 w-3.5" />
        ) : (
          <Icons.Copy className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
}

// ─── Components ───────────────────────────────────────────

function StatusBadge({ status }: { status: SyncStatus }) {
  const config = STATUS_CONFIG[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        config.bg,
        config.color,
      )}
      data-testid={`sync-badge-${status}`}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", config.dot)} />
      {config.label}
    </span>
  );
}

function EntryRow({ entry }: { entry: ManifestEntry }) {
  const fileName = entry.workshop.split("/").pop() ?? entry.workshop;
  const dirPath = entry.workshop
    .replace("workshop/", "")
    .replace(`/${fileName}`, "");

  const route = resolveRoute(entry.workshop);
  const isPreview = route.includes("/preview?");

  const content = (
    <>
      <div className="mt-0.5 shrink-0">
        <StatusBadge status={entry.status} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-white/90">{fileName}</span>
          <span className="truncate text-xs text-white/30">{dirPath}</span>
          {isPreview ? (
            <Icons.Eye className="ml-auto h-3 w-3 shrink-0 text-white/0 transition-colors group-hover:text-accent/50" />
          ) : (
            <Icons.ExternalLink className="ml-auto h-3 w-3 shrink-0 text-white/0 transition-colors group-hover:text-white/30" />
          )}
        </div>
        {entry.production && (
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-white/30">
            <Icons.ArrowRight className="h-3 w-3 shrink-0" />
            <span className="truncate">{entry.production}</span>
          </div>
        )}
        {entry.notes && (
          <p className="mt-0.5 text-xs text-white/40">{entry.notes}</p>
        )}
      </div>
    </>
  );

  return (
    <Link
      to={route}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "group flex items-start gap-3 rounded-lg border border-transparent px-3 py-2.5",
        "cursor-pointer transition-colors hover:border-white/[0.06] hover:bg-white/[0.02]",
      )}
      data-testid={`sync-entry-${entry.workshop}`}
    >
      {content}
    </Link>
  );
}

function SectionGroup({
  title,
  entries,
}: {
  title: string;
  entries: ManifestEntry[];
}) {
  if (entries.length === 0) return null;

  return (
    <div data-testid={`sync-section-${title.toLowerCase()}`}>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/40">
        {title}
        <span className="ml-2 text-white/20">{entries.length}</span>
      </h3>
      <div className="space-y-0.5">
        {entries.map((entry) => (
          <EntryRow key={entry.workshop} entry={entry} />
        ))}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────

export function SyncStatusPage() {
  const [filter, setFilter] = useState<FilterKey>("all");
  const search = useWorkshopSearch();

  const manifest = useMemo(() => parseManifest(syncManifestRaw), []);

  const allEntries = useMemo(
    () => [...manifest.pages, ...manifest.components],
    [manifest],
  );

  const counts = useMemo(() => {
    const c: Record<SyncStatus, number> = {
      synced: 0,
      modified: 0,
      new: 0,
      "workshop-only": 0,
    };
    for (const e of allEntries) {
      c[e.status]++;
    }
    return c;
  }, [allEntries]);

  // Apply both status filter and search query
  const matchesSearch = useMemo(() => {
    if (!search.trim()) return (_e: ManifestEntry) => true;
    const q = search.toLowerCase();
    return (e: ManifestEntry) =>
      e.workshop.toLowerCase().includes(q) ||
      (e.production?.toLowerCase().includes(q) ?? false) ||
      (e.notes?.toLowerCase().includes(q) ?? false) ||
      e.status.toLowerCase().includes(q);
  }, [search]);

  const filteredPages = useMemo(
    () =>
      manifest.pages
        .filter((e) => filter === "all" || e.status === filter)
        .filter(matchesSearch),
    [manifest, filter, matchesSearch],
  );

  const filteredComponents = useMemo(
    () =>
      manifest.components
        .filter((e) => filter === "all" || e.status === filter)
        .filter(matchesSearch),
    [manifest, filter, matchesSearch],
  );

  const total = allEntries.length;
  const actionable = counts.modified + counts.new;

  const filters: { key: FilterKey; label: string; count?: number }[] = [
    { key: "all", label: "All", count: total },
    { key: "modified", label: "Modified", count: counts.modified },
    { key: "new", label: "New", count: counts.new },
    { key: "synced", label: "Synced", count: counts.synced },
    { key: "workshop-only", label: "Workshop Only", count: counts["workshop-only"] },
  ];

  return (
    <div
      className="mx-auto max-w-3xl px-6 py-8"
      data-testid="sync-status-page"
    >
      {/* Page header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10">
          <Icons.GitBranch className="h-4.5 w-4.5 text-accent" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-white">Sync Status</h1>
          <p className="text-[11px] text-white/40">
            Last synced:{" "}
            <span className="text-white/50">
              {manifest.lastSyncDate || manifest.lastSyncCommit}
            </span>
            {manifest.lastSyncDate && (
              <code className="ml-1.5 rounded bg-white/5 px-1 py-0.5 font-mono text-white/30">
                {manifest.lastSyncCommit}
              </code>
            )}
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="mb-6 grid grid-cols-4 gap-3">
        {(
          [
            ["synced", counts.synced],
            ["modified", counts.modified],
            ["new", counts.new],
            ["workshop-only", counts["workshop-only"]],
          ] as [SyncStatus, number][]
        ).map(([status, count]) => {
          const config = STATUS_CONFIG[status];
          return (
            <button
              key={status}
              type="button"
              onClick={() => setFilter(filter === status ? "all" : status)}
              className={cn(
                "rounded-lg border px-3 py-2.5 text-left transition-all",
                filter === status
                  ? config.bg
                  : "border-white/[0.06] bg-white/[0.02] hover:border-white/10",
              )}
              data-testid={`sync-filter-${status}`}
            >
              <div
                className={cn(
                  "text-xl font-semibold",
                  filter === status ? config.color : "text-white/80",
                )}
              >
                {count}
              </div>
              <div className="mt-0.5 text-xs text-white/40">
                {config.label}
              </div>
            </button>
          );
        })}
      </div>

      {/* Action summary */}
      {actionable > 0 && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-amber-400/15 bg-amber-400/5 px-3 py-2">
          <Icons.AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-400" />
          <p className="text-xs text-amber-300/80">
            <strong>{actionable}</strong> component
            {actionable !== 1 ? "s" : ""} need attention — run{" "}
            <code className="rounded bg-white/5 px-1 py-0.5 font-mono text-amber-300">
              bun workshop:status --changed
            </code>{" "}
            in terminal for git-level diff details.
          </p>
        </div>
      )}

      {/* Filter tabs */}
      <div className="mb-4 flex items-center gap-1 border-b border-white/[0.06] pb-px">
        {filters.map(({ key, label, count }) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={cn(
              "relative px-3 py-2 text-xs font-medium transition-colors",
              filter === key
                ? "text-accent"
                : "text-white/40 hover:text-white/60",
            )}
            data-testid={`sync-tab-${key}`}
          >
            {label}
            {count !== undefined && count > 0 && (
              <span
                className={cn(
                  "ml-1.5 rounded-full px-1.5 py-0.5 text-[10px]",
                  filter === key
                    ? "bg-accent/15 text-accent"
                    : "bg-white/5 text-white/30",
                )}
              >
                {count}
              </span>
            )}
            {filter === key && (
              <span className="absolute bottom-0 left-0 right-0 h-px bg-accent" />
            )}
          </button>
        ))}
      </div>

      {/* Entries */}
      <div className="space-y-8">
        <SectionGroup title="Pages" entries={filteredPages} />
        <SectionGroup title="Components" entries={filteredComponents} />

        {filteredPages.length === 0 && filteredComponents.length === 0 && (
          <div className="py-12 text-center">
            <Icons.CheckCircle className="mx-auto h-8 w-8 text-emerald-400/50" />
            <p className="mt-3 text-sm text-white/40">
              No entries match this filter.
            </p>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="mt-10 border-t border-white/[0.06] pt-4">
        <p className="text-xs text-white/25">
          This report reads from{" "}
          <code className="text-white/35">workshop/sync-manifest.yaml</code>.
          Git-level drift detection is only available via CLI:
        </p>
        <TerminalCommand command="bun workshop:status --changed" />
      </div>
    </div>
  );
}
